import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { RotateCcw } from 'lucide-react';
import api from '@/lib/api';
import { formatTL, formatDate } from '@/lib/format';
import { Empty, StatusBadge } from '@/components/shared';

export default function AdminSales() {
  const [sales, setSales] = useState([]);
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState('');
  const [loading, setLoading] = useState(true);

  const load = (sid = '') => {
    setLoading(true);
    api.get('/admin/sales', { params: sid ? { storeId: sid } : {} })
      .then((r) => setSales(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get('/admin/stores').then((r) => setStores(r.data)).catch(() => {});
    load();
  }, []);

  const onFilter = (v) => { setStoreId(v); load(v); };

  const returnSale = async (id) => {
    if (!window.confirm('Bu satış iade/iptal edilecek ve komisyonu geri alınacak. Onaylıyor musunuz?')) return;
    try {
      await api.post(`/admin/sales/${id}/return`);
      toast.success('Satış iade edildi, komisyon geri alındı');
      load(storeId);
    } catch (e) { toast.error(e?.response?.data?.detail || 'İşlem başarısız'); }
  };

  const totalVol = sales.reduce((a, s) => a + (s.returned ? 0 : s.basketAmount), 0);
  const totalComm = sales.reduce((a, s) => a + (s.returned ? 0 : s.commissionAmount), 0);

  return (
    <div className="fade-up" data-testid="admin-sales">
      <h1 className="font-serif text-3xl text-navy mb-1">Satışlar</h1>
      <p className="text-navy/50 text-sm mb-6">Satış ve komisyon kayıtları</p>

      <div className="flex flex-wrap items-center gap-4 mb-6">
        <select
          value={storeId}
          onChange={(e) => onFilter(e.target.value)}
          data-testid="sales-store-filter"
          className="bg-cream border border-navy/15 rounded-md px-3 py-2 text-sm text-navy focus:outline-none"
        >
          <option value="">Tüm mağazalar</option>
          {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <div className="text-sm text-navy/60">Ciro: <span className="font-medium text-navy">{formatTL(totalVol)}</span></div>
        <div className="text-sm text-navy/60">Komisyon: <span className="font-medium text-greenx">{formatTL(totalComm)}</span></div>
      </div>

      {loading ? (
        <div className="text-navy/40 text-sm">Yükleniyor...</div>
      ) : sales.length === 0 ? (
        <Empty text="Satış kaydı yok" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left text-navy/50 text-xs border-b border-navy/10">
                <th className="py-3 pr-4 font-medium">Tarih</th>
                <th className="py-3 pr-4 font-medium">Mimar</th>
                <th className="py-3 pr-4 font-medium">Mağaza</th>
                <th className="py-3 pr-4 font-medium">Sepet</th>
                <th className="py-3 pr-4 font-medium">İndirim</th>
                <th className="py-3 pr-4 font-medium">Oran</th>
                <th className="py-3 pr-4 font-medium">Komisyon</th>
                <th className="py-3 pr-4 font-medium">Durum</th>
                <th className="py-3 pr-4 font-medium">İşlem</th>
              </tr>
            </thead>
            <tbody data-testid="sales-table">
              {sales.map((s) => (
                <tr key={s.id} className={`border-b border-navy/10 ${s.returned ? 'opacity-50' : ''}`}>
                  <td className="py-3 pr-4 text-navy/70">{formatDate(s.date)}</td>
                  <td className="py-3 pr-4 text-navy font-medium">{s.architectName}</td>
                  <td className="py-3 pr-4 text-navy/70">{s.storeName}</td>
                  <td className="py-3 pr-4 text-navy/70">{formatTL(s.basketAmount)}</td>
                  <td className="py-3 pr-4 text-redx">{formatTL(s.discountAmount)}</td>
                  <td className="py-3 pr-4 text-navy/70">%{(s.commissionRate * 100).toFixed(0)}</td>
                  <td className="py-3 pr-4 text-greenx font-medium">{formatTL(s.commissionAmount)}</td>
                  <td className="py-3 pr-4"><StatusBadge status={s.returned ? 'returned' : (s.commissionStatus || 'pending')} /></td>
                  <td className="py-3 pr-4">
                    {s.returned ? (
                      <span className="text-navy/30 text-xs">İade edildi</span>
                    ) : (
                      <button onClick={() => returnSale(s.id)} data-testid={`return-sale-${s.id}`}
                        className="inline-flex items-center gap-1 text-xs border border-redx/30 text-redx px-2.5 py-1.5 rounded-md active:scale-95 transition-transform">
                        <RotateCcw className="w-3 h-3" /> İade/İptal
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
